import React, { useContext } from 'react'
import type { CSSProperties } from 'react'
import { ComponentConfig, Slot, renderContext } from '@puckeditor/core'
import { ColorField } from 'breaktime-theme/src/puck/fields/ColorField'
import {
  BackgroundStyleProps,
  BorderStyleProps,
  TypographyProps,
  FontWeight,
  backgroundStyleField,
  borderStyleField,
  fontFamilyField,
  fontFamilyFallbackField,
  textTransformField,
  fontWeightOptions,
  BorderOverlayProps,
  borderOverlayField,
  borderOverlayStyle,
  applyBackgroundStyle,
  applyBorderStyle,
  applyTypography,
} from 'breaktime-theme/src/puck/fields/StyleFields'
import {
  Spacing,
  defaultSpacing,
  SpacingGrid,
} from 'breaktime-theme/src/puck/fields/SpacingField'

export type LayoutSpacing = { padding?: Spacing; margin?: Spacing }

export type PollStartScreenButtonProps = {
  label?: string
  background?: BackgroundStyleProps
  border?: BorderStyleProps
  textColor?: string
  bgHoverColor?: string
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  textTransform?: TypographyProps['textTransform']
  fontSize?: string
  fontWeight?: FontWeight
  layout?: LayoutSpacing
  borderOverlay?: BorderOverlayProps
}

export type StartScreenTextStyleProps = {
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  textTransform?: TypographyProps['textTransform']
  fontSize?: string
  fontWeight?: FontWeight
  color?: string
  lineHeight?: string
}

export type PollStartScreenCardProps = {
  headline?: string
  body?: string
  layout?: LayoutSpacing
  background?: BackgroundStyleProps
  border?: BorderStyleProps
  maxWidth?: string
  titleFontFamily?: TypographyProps['fontFamily']
  titleFontFamilyFallback?: TypographyProps['fontFamilyFallback']
  titleTextTransform?: TypographyProps['textTransform']
  titleFontSize?: string
  titleFontWeight?: FontWeight
  titleColor?: string
  bodyFontFamily?: TypographyProps['fontFamily']
  bodyFontFamilyFallback?: TypographyProps['fontFamilyFallback']
  bodyTextTransform?: TypographyProps['textTransform']
  bodyFontSize?: string
  bodyFontWeight?: FontWeight
  bodyColor?: string
  titleLineHeight?: string
  bodyLineHeight?: string
  borderOverlay?: BorderOverlayProps
  /** Spacing applied to the OUTER flex-item wrapper (positions the whole card
   * block, e.g. margin-left auto to right-align). */
  wrapperLayout?: LayoutSpacing
  button: Slot
}

/** Typography inline style — only the props the user set. */
export const buildStartScreenTextStyle = (
  t: StartScreenTextStyleProps,
): CSSProperties => ({
  ...applyTypography({
    fontFamily: t.fontFamily,
    fontFamilyFallback: t.fontFamilyFallback,
    textTransform: t.textTransform,
  }),
  ...(t.fontSize ? { fontSize: t.fontSize } : {}),
  ...(t.fontWeight ? { fontWeight: Number(t.fontWeight) || undefined } : {}),
  ...(t.color ? { color: t.color } : {}),
  ...(t.lineHeight ? { lineHeight: t.lineHeight } : {}),
})

/** Card container inline style — background, border, max width. */
export const buildStartScreenCardStyle = (c: {
  background?: BackgroundStyleProps
  border?: BorderStyleProps
  maxWidth?: string
  layout?: LayoutSpacing
  borderOverlay?: BorderOverlayProps
}): CSSProperties => ({
  ...applyBackgroundStyle(c.background),
  ...applyBorderStyle(c.border),
  ...(c.maxWidth ? { maxWidth: c.maxWidth } : {}),
  ...(borderOverlayStyle(c.borderOverlay) ? { position: 'relative' } : {}),
  // block-flow so the card's own margins position it (margin-left:auto +
  // maxWidth right-aligns, margin:auto centers). The Start Screen wrapper
  // is block, not centered flex, so these take effect.
  ...applySpacing(c.layout),
})

export type PollStartScreenProps = {
  background?: BackgroundStyleProps
  card: Slot
}

const cssVal = (v?: string) => {
  if (!v || v === '0') return undefined
  return /^\d+(\.\d+)?$/.test(v) ? `${v}px` : v
}

export function applySpacing(layout?: LayoutSpacing): CSSProperties {
  const p = layout?.padding
  const m = layout?.margin
  const css: CSSProperties = {}
  if (p?.top) css.paddingTop = cssVal(p.top)
  if (p?.right) css.paddingRight = cssVal(p.right)
  if (p?.bottom) css.paddingBottom = cssVal(p.bottom)
  if (p?.left) css.paddingLeft = cssVal(p.left)
  if (m?.top) css.marginTop = cssVal(m.top)
  if (m?.right) css.marginRight = cssVal(m.right)
  if (m?.bottom) css.marginBottom = cssVal(m.bottom)
  if (m?.left) css.marginLeft = cssVal(m.left)
  return css
}

const spacingField = {
  type: 'object' as const,
  label: 'Layout',
  objectFields: {
    padding: {
      type: 'custom' as const,
      label: 'Padding',
      render: ({ value, onChange }: any) => (
        <SpacingGrid
          title="Padding"
          value={typeof value === 'object' && value ? value : defaultSpacing}
          onChange={onChange}
        />
      ),
    },
    margin: {
      type: 'custom' as const,
      label: 'Margin',
      render: ({ value, onChange }: any) => (
        <SpacingGrid
          title="Margin"
          value={typeof value === 'object' && value ? value : defaultSpacing}
          onChange={onChange}
        />
      ),
    },
  },
}

export const POLL_START_SCREEN_BUTTON_DEFAULT_PROPS: PollStartScreenButtonProps =
  {
    label: 'Start',
    background: {},
    border: {},
    textColor: '',
    bgHoverColor: '',
    fontSize: '1rem',
    layout: { padding: defaultSpacing, margin: defaultSpacing },
  }

export const PuckPollStartScreenButton: ComponentConfig<PollStartScreenButtonProps> =
  {
    label: 'Start Screen Button',
    defaultProps: POLL_START_SCREEN_BUTTON_DEFAULT_PROPS,
    permissions: {
      drag: false,
      duplicate: false,
      delete: false,
    },
    fields: {
      label: { type: 'text', label: 'Button Label' },
      background: { ...backgroundStyleField, label: 'Background' },
      border: { ...borderStyleField, label: 'Border' },
      textColor: {
        type: 'custom',
        label: 'Text Color',
        render: (props: any) => <ColorField {...props} clearable />,
      },
      bgHoverColor: {
        type: 'custom',
        label: 'Hover Background Color',
        render: (props: any) => <ColorField {...props} clearable />,
      },
      fontFamily: fontFamilyField,
      fontFamilyFallback: fontFamilyFallbackField,
      textTransform: textTransformField,
      fontSize: { type: 'text', label: 'Font Size', placeholder: 'e.g. 16px, 1rem' },
      fontWeight: { type: 'select', label: 'Font Weight', options: fontWeightOptions },
      layout: spacingField,
      borderOverlay: borderOverlayField,
    },
    render: ({
      label,
      background,
      border,
      textColor,
      fontFamily,
      fontFamilyFallback,
      textTransform,
      fontSize,
      fontWeight,
      layout,
      borderOverlay,
    }) => {
      const overlay = borderOverlayStyle(borderOverlay)
      return (
        <button
          className="btn start-button"
          style={{
            ...applyBackgroundStyle(background),
            ...applyBorderStyle(border),
            ...applyTypography({ fontFamily, fontFamilyFallback, textTransform }),
            ...(textColor && { color: textColor }),
            ...(fontSize && { fontSize }),
            ...(fontWeight ? { fontWeight: Number(fontWeight) || undefined } : {}),
            ...applySpacing(layout),
            ...(overlay ? { position: 'relative', overflow: 'visible' } : {}),
          }}
        >
          {overlay && <span aria-hidden style={overlay} />}
          {label || 'Start'}
        </button>
      )
    },
  }

export const POLL_START_SCREEN_CARD_DEFAULT_PROPS: PollStartScreenCardProps = {
  headline: 'Sample Headline',
  body: 'Sample body text.',
  layout: { padding: defaultSpacing, margin: defaultSpacing },
  button: [
    {
      type: 'PollStartScreenButton',
      props: {
        id: 'PollStartScreenButton-default',
        ...POLL_START_SCREEN_BUTTON_DEFAULT_PROPS,
      },
    },
  ],
}

export const PuckPollStartScreenCard: ComponentConfig<PollStartScreenCardProps> =
  {
    label: 'Start Screen Card',
    defaultProps: POLL_START_SCREEN_CARD_DEFAULT_PROPS,
    permissions: {
      drag: false,
      duplicate: false,
      delete: false,
    },
    fields: {
      headline: { type: 'text', label: 'Headline' },
      body: { type: 'textarea', label: 'Body' },
      background: { ...backgroundStyleField, label: 'Card Background' },
      border: { ...borderStyleField, label: 'Card Border' },
      maxWidth: { type: 'text', label: 'Card Max Width', placeholder: '600px' },
      titleFontFamily: { ...fontFamilyField, label: 'Title Font Family' },
      titleFontFamilyFallback: { ...fontFamilyFallbackField, label: 'Title Font Family Fallback' },
      titleTextTransform: { ...textTransformField, label: 'Title Text Transform' },
      titleFontSize: { type: 'text', label: 'Title Font Size', placeholder: '24px' },
      titleFontWeight: { type: 'select', label: 'Title Font Weight', options: fontWeightOptions },
      titleLineHeight: { type: 'text', label: 'Title Line Height', placeholder: '1.1' },
      titleColor: {
        type: 'custom',
        label: 'Title Color',
        render: (props: any) => <ColorField {...props} clearable />,
      },
      bodyFontFamily: { ...fontFamilyField, label: 'Body Font Family' },
      bodyFontFamilyFallback: { ...fontFamilyFallbackField, label: 'Body Font Family Fallback' },
      bodyTextTransform: { ...textTransformField, label: 'Body Text Transform' },
      bodyFontSize: { type: 'text', label: 'Body Font Size', placeholder: '16px' },
      bodyFontWeight: { type: 'select', label: 'Body Font Weight', options: fontWeightOptions },
      bodyLineHeight: { type: 'text', label: 'Body Line Height', placeholder: '1.4' },
      bodyColor: {
        type: 'custom',
        label: 'Body Color',
        render: (props: any) => <ColorField {...props} clearable />,
      },
      layout: spacingField,
      wrapperLayout: {
        ...spacingField,
        label: 'Wrapper Layout (positions the whole card)',
      },
      borderOverlay: borderOverlayField,
      button: { type: 'slot' },
    },
    render: ({
      headline,
      body,
      layout,
      background,
      border,
      maxWidth,
      titleFontFamily,
      titleFontFamilyFallback,
      titleTextTransform,
      titleFontSize,
      titleFontWeight,
      titleColor,
      bodyFontFamily,
      bodyFontFamilyFallback,
      bodyTextTransform,
      bodyFontSize,
      bodyFontWeight,
      bodyColor,
      titleLineHeight,
      bodyLineHeight,
      borderOverlay,
      button: ButtonSlot,
    }) => (
      <div
        className="start-slide__content"
        style={buildStartScreenCardStyle({
          background,
          border,
          maxWidth,
          layout,
          borderOverlay,
        })}
      >
        {borderOverlayStyle(borderOverlay) && (
          <div aria-hidden style={borderOverlayStyle(borderOverlay)} />
        )}
        {headline && (
          <h2
            className="start-screen-headline step-content__title"
            style={buildStartScreenTextStyle({
              fontFamily: titleFontFamily,
              fontFamilyFallback: titleFontFamilyFallback,
              textTransform: titleTextTransform,
              fontSize: titleFontSize,
              fontWeight: titleFontWeight,
              color: titleColor,
              lineHeight: titleLineHeight,
            })}
            dangerouslySetInnerHTML={{ __html: headline }}
          />
        )}
        {body && (
          <p
            className="start-body start-screen-copy"
            style={buildStartScreenTextStyle({
              fontFamily: bodyFontFamily,
              fontFamilyFallback: bodyFontFamilyFallback,
              textTransform: bodyTextTransform,
              fontSize: bodyFontSize,
              fontWeight: bodyFontWeight,
              color: bodyColor,
              lineHeight: bodyLineHeight,
            })}
            dangerouslySetInnerHTML={{ __html: body }}
          />
        )}
        <ButtonSlot
          allow={['PollStartScreenButton']}
          className="no-slot-padding"
        />
      </div>
    ),
  }

export const POLL_START_SCREEN_DEFAULT_PROPS: PollStartScreenProps = {
  background: {},
  card: [
    {
      type: 'PollStartScreenCard',
      props: {
        id: 'PollStartScreenCard-default',
        ...POLL_START_SCREEN_CARD_DEFAULT_PROPS,
      },
    },
  ],
}

/** Read the card's `wrapperLayout` spacing so the Start Screen can apply it
 * to the flex-item wrapper (the card can't style its own puck slot parent).
 * renderContext.data holds the current tree in BOTH the editor and production
 * <Render> (usePuck would be editor-only and break render mode). */
const useCardWrapperLayout = (): LayoutSpacing | undefined => {
  const ctx = useContext(renderContext)
  const findCard = (nodes: any[]): any => {
    for (const n of nodes ?? []) {
      if (n?.type === 'PollStartScreenCard') return n
      for (const v of Object.values(n?.props ?? {})) {
        if (Array.isArray(v)) {
          const hit = findCard(v as any[])
          if (hit) return hit
        }
      }
    }
    return undefined
  }
  return findCard(ctx?.data?.content ?? [])?.props?.wrapperLayout
}

const StartScreenRender = ({
  background,
  CardSlot,
}: {
  background?: BackgroundStyleProps
  CardSlot: React.ComponentType<{ allow: string[] }>
}) => {
  const wrapperLayout = useCardWrapperLayout()
  return (
    <div
      className="start-slide"
      style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', ...applyBackgroundStyle(background) }}
    >
      <div className="container start-slide-container">
        {/* block flow so the wrapper spacing (margin auto etc) positions the
            whole card block */}
        <div
          className="start-slide-inner-content-wrapper"
          style={{ width: '100%', ...applySpacing(wrapperLayout) }}
        >
          <CardSlot allow={['PollStartScreenCard']} />
        </div>
      </div>
    </div>
  )
}

export const PuckPollStartScreen: ComponentConfig<PollStartScreenProps> = {
  label: 'Start Screen',
  defaultProps: POLL_START_SCREEN_DEFAULT_PROPS,
  permissions: {
    duplicate: false,
  },
  fields: {
    background: { ...backgroundStyleField, label: 'Background' },
    card: { type: 'slot', label: 'Card' },
  },
  render: ({ background, card: CardSlot }) => (
    <StartScreenRender background={background} CardSlot={CardSlot as any} />
  ),
}
